/**
 * Add client's pure rules. The body the dialog sends carries the name
 * parts name-cased (the Enter path sends without a blur, so the case is
 * applied here as well as in the box), the service, the lead boxes the
 * service shows (a hidden box's pick is never sent), the pronouns, and
 * the rate keys only when a set is offered and chosen. No display name
 * is ever sent: the route derives it from the parts. The duplicate check
 * compares the typed first and last name with the roster's display
 * names, ignoring case and spacing only, and offers an active client
 * before an archived one, then the newest.
 */

import type { ClientEngagementValue, ClientRosterRowWire } from '@/lib/clientsApi';
import { nameCase as toNameCase } from '@/lib/valueCase';

export interface AddClientForm {
  first: string;
  last: string;
  preferred: string;
  engagement: ClientEngagementValue;
  /** '' = none chosen. */
  cmRosterId: string;
  /** '' = none chosen. */
  therapistRosterId: string;
  /** '' = not recorded. */
  pronouns: string;
  /** '' = no rate set. */
  rateSetId: string;
}

export interface CreateClientBody {
  first_name: string;
  last_name: string | null;
  preferred_name: string | null;
  engagement: ClientEngagementValue;
  primary_cm_roster_id: string | null;
  spice_therapist_roster_id: string | null;
  pronouns: string | null;
  rate_set_id?: string;
  rate_effective_from?: string;
}

/** The Care manager box shows for care management and for both. */
export function showsCareManager(e: ClientEngagementValue): boolean {
  return e === 'care_management' || e === 'both';
}

/** The SPICE therapist box shows for SPICE and for both. */
export function showsSpiceTherapist(e: ClientEngagementValue): boolean {
  return e === 'spice' || e === 'both';
}

/** A trimmed optional part: blank is null, otherwise its name case. */
function optionalPart(v: string): string | null {
  const trimmed = v.trim();
  return trimmed === '' ? null : toNameCase(trimmed, 'person');
}

export function addClientBody(form: AddClientForm, opts: { offerRateSets: boolean; today: string }): CreateClientBody {
  return {
    first_name: toNameCase(form.first.trim(), 'person'),
    last_name: optionalPart(form.last),
    preferred_name: optionalPart(form.preferred),
    engagement: form.engagement,
    primary_cm_roster_id: showsCareManager(form.engagement) ? form.cmRosterId || null : null,
    spice_therapist_roster_id: showsSpiceTherapist(form.engagement) ? form.therapistRosterId || null : null,
    pronouns: form.pronouns || null,
    ...(opts.offerRateSets && form.rateSetId !== '' ? { rate_set_id: form.rateSetId, rate_effective_from: opts.today } : {}),
  };
}

/** A name with its spacing made one space and its case dropped. */
function foldName(s: string): string {
  return s.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** True when the two names differ only in case and spacing. */
export function sameClientName(a: string, b: string): boolean {
  return foldName(a) === foldName(b);
}

/** The roster row a typed first and last name already belong to: an
 *  active row before an archived one, then the newest; null with no
 *  match or no first name. The comparison is on the typed text, so name
 *  case never changes the answer. */
export function duplicateClient(rows: readonly ClientRosterRowWire[], first: string, last: string): ClientRosterRowWire | null {
  if (first.trim() === '') return null;
  const candidate = [first, last]
    .map((p) => p.trim())
    .filter((p) => p !== '')
    .join(' ');
  const matches = rows
    .filter((r) => sameClientName(r.display_name, candidate))
    .sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return b.created_at.localeCompare(a.created_at);
    });
  return matches[0] ?? null;
}
